import React, { useContext } from 'react';
import { useState } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CompanyMutations } from '../../../../../graphql/company.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import {
  BarcodeFormat,
  barcodeFormatToString,
  Company,
  ScanConfiguration,
  ScanConfigurationType,
  scanConfigurationTypeToString,
} from '../../../../../types/company';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { Button, ButtonVariant, CancelButton } from '../../../../components/Button';
import { DropdownSearchSelect } from '../../../../components/Dropdown';
import { ActionModal } from '../../../../components/Modal';
import { TextInput } from '../../../../components/TextInput';
import { ToastType } from '../../../../components/Toast';

class ScanConfigurationInput {
  constructor(configuration?: ScanConfiguration | ScanConfigurationInput) {
    this.regex = configuration?.regex;
    this.regexOption = configuration?.regexOption;
    this.barcodeFormat = configuration?.barcodeFormat;
    this.type = configuration?.type;
    this.typeId = configuration?.typeId;
  }

  regex: string | undefined;
  regexOption?: string | undefined;
  barcodeFormat: BarcodeFormat | undefined;
  type: ScanConfigurationType | undefined;
  typeId?: string | undefined;

  withRegex(regex: string | undefined): ScanConfigurationInput {
    this.regex = regex;
    return new ScanConfigurationInput(this);
  }

  withBarcodeFormat(barcodeFormat: BarcodeFormat | undefined): ScanConfigurationInput {
    this.barcodeFormat = barcodeFormat;
    return new ScanConfigurationInput(this);
  }

  withType(type: ScanConfigurationType | undefined): ScanConfigurationInput {
    this.type = type;
    return new ScanConfigurationInput(this);
  }

  isValid() {
    if (!this.regex || !this.barcodeFormat || !this.type) return false;

    return true;
  }

  toScanConfiguration(): ScanConfiguration {
    if (!this.isValid()) throw Error('Invalid ScanConfigurationInput state');
    return {
      regex: this.regex!,
      regexOption: this.regexOption,
      typeId: this.typeId,
      barcodeFormat: this.barcodeFormat!,
      type: this.type!,
    };
  }
}

interface BarcodeConfigurationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  configuration: ScanConfiguration | undefined;
  onComplete: (configuration: ScanConfigurationInput | undefined) => Company;
}

export default function BarcodeConfigurationModal({
  open,
  setOpen,
  configuration,
  onComplete,
}: BarcodeConfigurationModalProps) {
  const { setCurrentCompany } = useContext(CompanyContext);

  const [input, setInput] = useState<ScanConfigurationInput>(new ScanConfigurationInput(configuration));

  const { showToast } = useToast();

  const [updateCompany, { loading: updateCompanyLoading }] = useMutation(CompanyMutations.update, {
    onCompleted: response => {
      setCurrentCompany(new Company(response.updateCompany));
      setOpen(false);
    },
  });

  const handleComplete = async (input: ScanConfigurationInput | undefined) => {
    const company = onComplete(input);
    await updateCompany({ variables: { company: company.forUpdate() } });
    if (!input) showToast({ type: ToastType.success, title: 'Configuration removed successfully!' });
    else {
      showToast({
        type: ToastType.success,
        title: configuration ? 'Configuration updated successfully!' : 'Configuration created successfully!',
      });
    }
  };

  const hasChanges = (() => {
    if (!configuration) return true;
    if (configuration.regex !== input.regex) return true;
    if (configuration.type !== input.type) return true;
    if (configuration.barcodeFormat !== input.barcodeFormat) return true;
    return false;
  })();

  const footer = (
    <div className='flex justify-between gap-2'>
      {configuration ? (
        <Button
          variant={ButtonVariant.danger}
          text={t(TranslationKey.delete).singular.upper}
          onClick={() => handleComplete(undefined)}
          loading={updateCompanyLoading}
        />
      ) : (
        <div></div>
      )}

      <div className='flex gap-2'>
        <CancelButton onClick={() => setOpen(false)} />
        <Button
          variant={ButtonVariant.primary}
          text={configuration ? t(TranslationKey.update).singular.upper : t(TranslationKey.create).singular.upper}
          onClick={() => handleComplete(input)}
          disabled={!input.isValid() || !hasChanges}
          loading={updateCompanyLoading}
        />
      </div>
    </div>
  );

  return (
    <ActionModal
      title={
        configuration
          ? t(TranslationKey.updateBarcodeConfiguration).singular.upper
          : t(TranslationKey.createBarcodeConfiguration).singular.upper
      }
      open={open}
      setOpen={setOpen}
      footer={footer}
    >
      <div className='grid min-w-[400px] gap-4'>
        <DropdownSearchSelect
          mandatory
          label={t(TranslationKey.type).singular.upper}
          value={input.type}
          items={Object.values(ScanConfigurationType)}
          onSelect={value => setInput(input.withType(value))}
          placeholder={'Select a barcode configuration type'}
          textBuilder={type => scanConfigurationTypeToString(type)}
        />
        <DropdownSearchSelect
          mandatory
          label={t(TranslationKey.barcodeFormat).singular.upper}
          value={input.barcodeFormat}
          items={Object.values(BarcodeFormat)}
          onSelect={value => setInput(input.withBarcodeFormat(value))}
          placeholder={'Choose a barcode format'}
          textBuilder={type => barcodeFormatToString(type)}
        />
        <TextInput
          label={t(TranslationKey.regexInput).singular.upper}
          placeholder={'e.g., ^[A-Z]{3}d{4}$ (3 letters followed by 4 digits)'}
          mandatory
          value={input.regex}
          onChange={value => setInput(input.withRegex(value))}
        />
      </div>
    </ActionModal>
  );
}
